import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion as m, useScroll, useSpring } from "framer-motion";
import EditorialCover from "../components/EditorialCover";
import ArticleCard from "../components/ArticleCard";
import { Button, Container, Reveal, Section, Tag } from "../components/ui";
import { articles, getArticleBySlug } from "../data/articles";
import { site } from "../data/site";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const Article = () => {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 160, damping: 28 });

  if (!article) return <Navigate to="/blog" replace />;

  const { title, dek, body, category, date, readTime, img, accent, tags = [] } =
    article;

  const related = articles
    .filter((a) => a.slug !== slug && a.category === category)
    .slice(0, 3);
  const fallback = articles.filter((a) => a.slug !== slug).slice(0, 3);
  const suggestions = related.length >= 2 ? related : fallback;

  const index = articles.findIndex((a) => a.slug === slug);
  const next = articles[index + 1] || articles[0];

  return (
    <>
      <Helmet>
        <title>{title} — Jephthah Boateng</title>
        <meta name="description" content={dek} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={dek} />
        <meta property="og:type" content="article" />
      </Helmet>

      <m.div
        style={{ scaleX }}
        className="fixed left-0 top-0 z-[70] h-[2px] w-full origin-left bg-gradient-electric"
        aria-hidden="true"
      />

      {/* Masthead */}
      <header className="border-b border-[var(--color-border)] bg-paper-warm pb-14 pt-36 dark:bg-night-surface md:pt-44">
        <Container>
          <Reveal>
            <Link
              to="/blog"
              className="link-underline font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted dark:text-slightwhite/55"
            >
              ← All writing
            </Link>
          </Reveal>

          <Reveal delay={0.08}>
            <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted dark:text-slightwhite/55">
              <span className="text-azure dark:text-electric">{category}</span>
              <span className="h-px w-6 bg-[var(--color-border-strong)]" />
              <span>{formatDate(date)}</span>
              <span className="h-px w-6 bg-[var(--color-border-strong)]" />
              <span>{readTime} min read</span>
            </div>
          </Reveal>

          <Reveal delay={0.14}>
            <h1 className="t-hero mt-7 max-w-4xl">{title}</h1>
          </Reveal>

          <Reveal delay={0.2}>
            <p className="t-lead mt-7 max-w-prose font-display text-2xl italic leading-snug md:text-[1.75rem]">
              {dek}
            </p>
          </Reveal>

          <Reveal delay={0.26}>
            <div className="mt-10 flex items-center gap-4">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-azure font-display text-sm text-paper dark:bg-electric dark:text-paper">
                {site.initials}
              </span>
              <div>
                <p className="text-sm font-medium">{site.name}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-muted dark:text-slightwhite/55">
                  {site.role}
                </p>
              </div>
            </div>
          </Reveal>
        </Container>
      </header>

      {/* Cover */}
      <Container className="-mt-0 pt-12">
        <Reveal>
          <div className="overflow-hidden rounded-lg bg-paper-warm dark:bg-night-surface">
            {img ? (
              <img
                src={img}
                alt={title}
                className="max-h-[60vh] w-full object-cover"
              />
            ) : (
              <div className="aspect-[16/7]">
                <EditorialCover title={title} kicker={category} accent={accent} />
              </div>
            )}
          </div>
        </Reveal>
      </Container>

      {/* Body */}
      <Section tone="default" className="!pt-16">
        <div className="grid gap-12 lg:grid-cols-12">
          <aside className="lg:col-span-3">
            <div className="sticky top-28 space-y-7">
              <div>
                <p className="t-eyebrow">Filed under</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {tags.map((t) => (
                    <Tag key={t} variant="azure">
                      {t}
                    </Tag>
                  ))}
                </div>
              </div>
              <div className="rule" />
              <div>
                <p className="t-eyebrow">Share</p>
                <div className="mt-4 flex flex-col gap-2 text-sm">
                  <a
                    className="link-underline w-fit"
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                      title
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    X / Twitter
                  </a>
                  <a
                    className="link-underline w-fit"
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                      typeof window !== "undefined" ? window.location.href : ""
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    LinkedIn
                  </a>
                  <a
                    className="link-underline w-fit"
                    href={`mailto:?subject=${encodeURIComponent(title)}`}
                  >
                    Email
                  </a>
                </div>
              </div>
            </div>
          </aside>

          <article className="prose-editorial lg:col-span-8 lg:col-start-4">
            {body.map((paragraph, i) => (
              <m.p
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              >
                {paragraph}
              </m.p>
            ))}

            <div className="mt-14 border-t border-[var(--color-border)] pt-9">
              <p className="t-eyebrow">End</p>
              <p className="mt-4 max-w-prose font-display text-2xl italic leading-snug">
                Written by {site.name}.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Button to="/contact" size="sm" withArrow>
                  Reply to this
                </Button>
                <Button to="/blog" variant="outline" size="sm">
                  Back to the archive
                </Button>
              </div>
            </div>
          </article>
        </div>
      </Section>

      {/* Next */}
      <Section tone="warm" className="!py-16">
        <Link
          to={`/blog/${next.slug}`}
          className="group flex flex-col gap-3 md:flex-row md:items-end md:justify-between"
        >
          <div>
            <p className="t-eyebrow">Read next</p>
            <h3 className="mt-4 max-w-2xl font-display text-3xl leading-tight transition-colors duration-300 group-hover:text-azure dark:group-hover:text-electric md:text-4xl">
              {next.title}
            </h3>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted dark:text-slightwhite/55">
            {next.category} · {next.readTime} min
          </span>
        </Link>
      </Section>

      {/* Related */}
      <Section tone="default">
        <p className="t-eyebrow">More like this</p>
        <div className="mt-10 grid gap-x-8 gap-y-14 md:grid-cols-3">
          {suggestions.map((a, i) => (
            <ArticleCard key={a.slug} article={a} index={i} />
          ))}
        </div>
      </Section>
    </>
  );
};

export default Article;
